import type { Event } from '@bugsnag/js'

/** The app's own bundle, whichever domain it is served from (white labels included) */
const appBundleFile = /\/_next\/static\//

/**
 * Errors that are expected or come from the browser rather than the app.
 * They can't be fixed here and only add noise in Bugsnag.
 */
const ignoredMessages = [
  // The SignalR connection was cancelled because the page was left or reloaded mid-handshake
  /Handshake was canceled/i,
  // Firebase Auth could not reach Google because the device is offline
  /auth\/network-request-failed/,
  // Browser notice that a resize observer callback was delivered a frame late
  /ResizeObserver loop/,
  // Firefox: a browser extension left `window.event` pointing at a dead object
  /can't access dead object/,
  // Adyen's postMessage listener receiving a message without an origin from
  // outside the page (for example an Android web view bridge). The app itself
  // only reads `origin` from `window.location`
  /^Cannot read properties of undefined \(reading 'origin'\)$/,
]

const staleAssetMessage = /Loading (CSS )?chunk \S+ failed|Unexpected token '<'/

const staleAssetReloadKey = 'bugsnagStaleAssetReloadAt'

/** A page that still fails to load its files this soon after a reload is reported instead */
const staleAssetReloadInterval = 60 * 1000

/**
 * Whether the error was thrown without any of the app's own code on the stack,
 * for example by a browser extension, a script injected by an in-app browser
 * or a third-party SDK failing on its own.
 * Errors without a stack trace are kept, since there is nothing to judge them by.
 */
export function isForeignError(event: Event) {
  const frames = event.errors.flatMap(error => error.stacktrace)

  return (
    frames.length > 0 &&
    !frames.some(frame => appBundleFile.test(frame.file ?? ''))
  )
}

export function isIgnoredError(event: Event) {
  return event.errors.some(error =>
    ignoredMessages.some(message => message.test(error.errorMessage)),
  )
}

/**
 * Whether a page opened before a deploy failed to load a file the deploy
 * removed. The server answers those requests with an HTML page, hence
 * `Unexpected token '<'` when the browser parses it as a script.
 */
export function isStaleAssetError(event: Event) {
  return event.errors.some(
    error =>
      error.errorClass === 'ChunkLoadError' ||
      staleAssetMessage.test(error.errorMessage),
  )
}

/**
 * Reloads the page to pick up the files of the current deploy, unless it was
 * already reloaded for this a moment ago.
 * @returns whether the page is reloading
 */
export function reloadForStaleAssets() {
  try {
    const lastReloadAt = Number(sessionStorage.getItem(staleAssetReloadKey))

    if (lastReloadAt && Date.now() - lastReloadAt < staleAssetReloadInterval) {
      return false
    }

    sessionStorage.setItem(staleAssetReloadKey, String(Date.now()))
  } catch {
    return false
  }

  window.location.reload()

  return true
}

/**
 * Bugsnag `onError` callback.
 * @returns false to drop the event instead of sending it
 */
export default function bugsnagEventFilter(event: Event) {
  if (isForeignError(event) || isIgnoredError(event)) {
    return false
  }

  if (isStaleAssetError(event) && reloadForStaleAssets()) {
    return false
  }

  return true
}
